// src/pages/Customer/OrderTracking.jsx - SUPER OPTIMIZED

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, ChefHat, Package as PackageIcon, ShoppingBag, AlertCircle, Home, Star } from 'lucide-react'; // ADDED Star
import StorageService from '../../services/StorageService';

const OrderTracking = ({ sessionId, onBackToMenu }) => {
  const [orderData, setOrderData] = useState(null);
  const [session, setSession] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ OPTIMIZED: Load order status with smart caching
  const loadOrderStatus = useCallback(async () => {
    try {
      console.log('🔍 Loading order status for session:', sessionId);
      
      // ✅ Use cache for faster loads
      const data = await StorageService.getData();
      const currentSession = data.customerSessions?.[sessionId];
      
      if (!currentSession) {
        console.error('❌ Session not found:', sessionId);
        setError('Session not found. Please try placing your order again.');
        setInitialLoading(false);
        return;
      }
      
      setSession(currentSession);
      
      // ✅ Get restaurant from cache
      const rest = data.restaurants.find(r => r.id === currentSession.restaurantId);
      
      if (!rest) {
        console.error('❌ Restaurant not found:', currentSession.restaurantId);
        setError('Restaurant not found.');
        setInitialLoading(false);
        return;
      }
      
      setRestaurant(rest);
      
      const order = rest.orders.find(o => o.id === currentSession.orderId);
      
      if (!order) {
        console.error('❌ Order not found:', currentSession.orderId);
        setError('Order not found. Please contact staff.');
        setInitialLoading(false);
        return;
      }
      
      console.log('✅ Order found:', order);
      setOrderData(order);
      setError(null);
      
      // End session if order is delivered
      if (order.status === 'Delivered' && currentSession.active) {
        await StorageService.endSession(sessionId);
      }
    } catch (error) {
      console.error('❌ Error loading order status:', error);
      setError('Failed to load order. Please refresh the page.');
    } finally {
      if (initialLoading) {
        setInitialLoading(false);
      }
    }
  }, [sessionId, initialLoading]);

  // ✅ OPTIMIZED: Initial load and polling
  useEffect(() => {
    loadOrderStatus();
    
    // ✅ Poll every 3 seconds (reduced from constant polling)
    const interval = setInterval(loadOrderStatus, 3000);
    return () => clearInterval(interval);
  }, [loadOrderStatus]);

  // ✅ OPTIMIZED: Timer for remaining time calculation
  useEffect(() => {
    if (!orderData || orderData.status === 'Delivered' || !orderData.preparationStartTime || !orderData.preparationTime) {
      setRemainingTime(null);
      return;
    }

    const updateRemainingTime = () => {
      const startTime = new Date(orderData.preparationStartTime).getTime();
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);
      const prepTimeInSeconds = orderData.preparationTime * 60;
      const remaining = Math.max(0, prepTimeInSeconds - elapsed);
      
      setRemainingTime(remaining);
    };

    updateRemainingTime();
    const timer = setInterval(updateRemainingTime, 1000);
    
    return () => clearInterval(timer);
  }, [orderData?.preparationStartTime, orderData?.preparationTime, orderData?.status]);

  // ✅ MEMOIZED: Format time function
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // ✅ MEMOIZED: Calculate progress percentage
  const progressPercentage = useMemo(() => {
    if (!orderData?.preparationStartTime || !orderData?.preparationTime) return 0;
    
    const startTime = new Date(orderData.preparationStartTime).getTime();
    const now = Date.now();
    const elapsed = Math.floor((now - startTime) / 1000);
    const prepTimeInSeconds = orderData.preparationTime * 60;
    
    return Math.min(100, (elapsed / prepTimeInSeconds) * 100);
  }, [orderData?.preparationStartTime, orderData?.preparationTime, remainingTime]);

  // ✅ MEMOIZED: Status steps
  const statusSteps = useMemo(() => [
    { status: 'Pending', label: 'Order Received', icon: ShoppingBag },
    { status: 'Preparing', label: 'Preparing', icon: ChefHat },
    { status: 'Ready', label: 'Ready', icon: PackageIcon },
    { status: 'Delivered', label: 'Delivered', icon: CheckCircle }
  ], []);

  // ✅ MEMOIZED: Current step calculation
  const currentStep = useMemo(() => {
    if (!orderData) return 0;
    const steps = ['Pending', 'Preparing', 'Ready', 'Delivered'];
    return steps.indexOf(orderData.status);
  }, [orderData?.status]);

  // 🟢 NEW: Google Rating Handler
  const handleRateOnGoogle = useCallback(() => {
    if (!restaurant?.googlePlaceId) {
      console.warn('❌ Google Place ID is missing. Cannot redirect for rating.');
      alert('The restaurant has not configured its Google Place ID for external ratings.');
      return;
    }

    const reviewUrl = `https://search.google.com/local/writereview?placeid=${restaurant.googlePlaceId}`;
    window.open(reviewUrl, '_blank', 'noopener,noreferrer');
  }, [restaurant?.googlePlaceId]);

  // Show error state
  if (error && !initialLoading) {
    return (
      <ErrorState error={error} onBack={onBackToMenu} />
    );
  }

  // Show loading only on initial load
  if (initialLoading) {
    return <LoadingState sessionId={sessionId} />;
  }

  // If data not loaded yet after initial loading
  if (!orderData || !restaurant) {
    return <LoadingState sessionId={sessionId} message="Fetching order details..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 shadow-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={onBackToMenu}
            className="mb-4 text-white hover:text-orange-100 flex items-center gap-2 transition-colors"
          >
            <Home className="w-5 h-5" /> Back to Menu
          </button>
          <h1 className="text-3xl font-bold mb-2">Track Your Order</h1>
          <p className="text-orange-100">{restaurant.name}</p>
          {orderData.tableName && (
            <p className="text-orange-100 text-sm">📍 {orderData.tableName}</p>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* Order Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-6"
        >
          <OrderHeader orderData={orderData} />

          {/* Estimated Time Progress */}
          {orderData.preparationTime && orderData.status !== 'Delivered' && orderData.preparationStartTime && (
            <TimeProgress
              estimatedTime={orderData.preparationTime}
              remainingTime={remainingTime}
              formatTime={formatTime}
              progressPercentage={progressPercentage}
            />
          )}

          {/* Waiting for confirmation */}
          {orderData.status === 'Pending' && !orderData.preparationTime && (
            <WaitingMessage />
          )}

          {/* Progress Steps */}
          <ProgressSteps
            statusSteps={statusSteps}
            currentStep={currentStep}
            orderStatus={orderData.status}
          />

          {/* Order Items */}
          <OrderItems items={orderData.itemDetails} />

          {/* Delivered Message */}
          {orderData.status === 'Delivered' && (
            <DeliveredMessage 
              onBackToMenu={onBackToMenu} 
              onRateOnGoogle={handleRateOnGoogle} // <-- Pass the new handler
            />
          )}
        </motion.div>

        {/* Help Section */}
        <HelpSection />
      </div>
    </div>
  );
};

// ✅ OPTIMIZED: Separate components to prevent unnecessary re-renders

const ErrorState = React.memo(({ error, onBack }) => (
  <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center p-6">
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
      <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h2>
      <p className="text-gray-600 mb-6">{error}</p>
      <button
        onClick={onBack}
        className="bg-orange-500 text-white px-6 py-3 rounded-xl hover:bg-orange-600 font-semibold transition-colors"
      >
        Back to Menu
      </button>
    </div>
  </div>
));

ErrorState.displayName = 'ErrorState';

const LoadingState = React.memo(({ sessionId, message = 'Loading your order...' }) => (
  <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center">
    <div className="text-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      >
        <ChefHat className="w-16 h-16 text-orange-500 mx-auto mb-4" />
      </motion.div>
      <p className="text-gray-600 text-lg">{message}</p>
      <p className="text-gray-400 text-sm mt-2">Session: {sessionId}</p>
    </div>
  </div>
));

LoadingState.displayName = 'LoadingState';

const OrderHeader = React.memo(({ orderData }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'text-yellow-600 bg-yellow-50 border border-yellow-200';
      case 'Preparing': return 'text-blue-600 bg-blue-50 border border-blue-200';
      case 'Ready': return 'text-green-600 bg-green-50 border border-green-200';
      case 'Delivered': return 'text-gray-600 bg-gray-50 border border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border border-gray-200';
    }
  };

  return (
    <div className="flex justify-between items-start mb-6 pb-6 border-b">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Order #{orderData.id}</h2>
        <p className="text-gray-600 mt-1">{orderData.customerName}</p>
        <p className="text-sm text-gray-500">{new Date(orderData.time).toLocaleString()}</p>
      </div>
      <div className="text-right">
        <p className="text-3xl font-bold text-orange-500 mb-2">₹{orderData.total}</p>
        <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(orderData.status)}`}>
          {orderData.status}
        </span>
      </div>
    </div>
  );
});

OrderHeader.displayName = 'OrderHeader';

const TimeProgress = React.memo(({ estimatedTime, remainingTime, formatTime, progressPercentage }) => (
  <motion.div
    initial={{ scale: 0.95 }}
    animate={{ scale: 1 }}
    className="bg-gradient-to-r from-orange-100 to-yellow-100 p-5 rounded-xl mb-6"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <Clock className="w-6 h-6 text-orange-600" />
        <div>
          <p className="text-sm text-gray-600">Estimated Time</p>
          <p className="text-2xl font-bold text-orange-600">{estimatedTime} mins</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm text-gray-600">Time Remaining</p>
        <p className="text-lg font-semibold text-gray-700">
          {remainingTime !== null ? formatTime(remainingTime) : '--:--'}
        </p>
      </div>
    </div>
    {remainingTime !== null && (
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <motion.div
          className="bg-orange-500 h-2.5 rounded-full"
          initial={{ width: '0%' }}
          animate={{ width: `${progressPercentage}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
    )}
  </motion.div>
));

TimeProgress.displayName = 'TimeProgress';

const WaitingMessage = React.memo(() => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6"
  >
    <div className="flex items-center gap-3">
      <Clock className="w-6 h-6 text-yellow-600" />
      <div>
        <p className="text-sm font-semibold text-yellow-800">Waiting for confirmation</p>
        <p className="text-xs text-yellow-600">The restaurant will provide an estimated time shortly</p>
      </div>
    </div>
  </motion.div>
));

WaitingMessage.displayName = 'WaitingMessage';

const ProgressSteps = React.memo(({ statusSteps, currentStep, orderStatus }) => (
  <div className="relative mb-8">
    {/* Progress Line */}
    <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200">
      <motion.div
        className="h-full bg-orange-500"
        initial={{ width: '0%' }}
        animate={{ width: `${(currentStep / 3) * 100}%` }}
        transition={{ duration: 0.5 }}
      />
    </div>

    {/* Steps */}
    <div className="relative grid grid-cols-4 gap-4">
      {statusSteps.map((step, index) => {
        const Icon = step.icon;
        const isActive = currentStep >= index;
        const isCurrent = currentStep === index;

        return (
          <div key={step.status} className="flex flex-col items-center">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: isCurrent ? [1, 1.1, 1] : 1 }}
              transition={{ duration: 1, repeat: isCurrent ? Infinity : 0 }}
              className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 transition-colors ${
                isActive ? 'bg-orange-500' : 'bg-gray-200'
              }`}
            >
              <Icon className={`w-8 h-8 ${isActive ? 'text-white' : 'text-gray-400'}`} />
            </motion.div>
            <p className={`text-sm font-medium text-center ${
              isActive ? 'text-gray-800' : 'text-gray-400'
            }`}>
              {step.label}
            </p>
          </div>
        );
      })}
    </div>
  </div>
));

ProgressSteps.displayName = 'ProgressSteps';

const OrderItems = React.memo(({ items }) => (
  <div className="pt-6 border-t">
    <h3 className="text-lg font-bold text-gray-800 mb-4">Order Items</h3>
    <div className="space-y-3">
      {items?.map((item, idx) => (
        <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-800">{item.name}</p>
            <p className="text-sm text-gray-600">₹{item.price} × {item.quantity}</p>
          </div>
          <p className="font-bold text-orange-600">₹{item.price * item.quantity}</p>
        </div>
      ))}
    </div>
  </div>
));

OrderItems.displayName = 'OrderItems';

const DeliveredMessage = React.memo(({ onBackToMenu, onRateOnGoogle }) => ( // <-- ADDED onRateOnGoogle
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className="mt-6 bg-green-50 border border-green-200 p-6 rounded-xl text-center"
  >
    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-3" />
    <h3 className="text-2xl font-bold text-green-700 mb-2">Order Delivered!</h3>
    <p className="text-green-600 mb-4">Thank you for ordering with us. Enjoy your meal! 🍽️</p>
    
    <div className="flex flex-col sm:flex-row justify-center gap-3">
        {/* 🟢 NEW GOOGLE RATING BUTTON */}
        <button
          onClick={onRateOnGoogle}
          className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 font-semibold transition-colors flex items-center justify-center gap-2"
        >
          <Star className="w-5 h-5 fill-current" /> Rate on Google
        </button>

        <button
          onClick={onBackToMenu}
          className="bg-orange-500 text-white px-6 py-3 rounded-xl hover:bg-orange-600 font-semibold transition-colors"
        >
          Order Again
        </button>
    </div>
  </motion.div>
));

DeliveredMessage.displayName = 'DeliveredMessage';

const HelpSection = React.memo(() => (
  <div className="bg-white rounded-xl shadow-md p-6 text-center">
    <AlertCircle className="w-12 h-12 text-blue-500 mx-auto mb-3" />
    <p className="text-gray-600">
      Need help with your order? Please contact our staff at your table.
    </p>
  </div>
));

HelpSection.displayName = 'HelpSection';

export default OrderTracking;